import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Loader2, CheckCircle2, Sparkles, Crown, Building2, Rocket, Briefcase, Tag,
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { useAuth } from '../../contexts/AuthContext';
import { cn } from '../../lib/utils';
import { recordPendingCheckout } from '../../lib/pendingCheckout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PLAN_ICONS = {
  SMALL_BUSINESS: Building2,
  GROWTH: Rocket,
  ENTERPRISE: Crown,
  INDEPENDENT: Briefcase,
  AGENCY: Sparkles,
  CONSULTANCY_ENTERPRISE: Crown,
};

/**
 * Subscription page — picks plan + seats + pursuit slots + cycle, shows
 * server-quoted price, then redirects to Stripe Checkout.
 *
 * The frontend only sends plan_id + seats + slots — backend computes
 * the actual amount, so users can't tamper with prices.
 */
export default function SubscribePage() {
  const navigate = useNavigate();
  const { authToken, user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [foundingCoupon, setFoundingCoupon] = useState(null);
  const [orgState, setOrgState] = useState(null);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [seats, setSeats] = useState(1);
  const [pursuitSlots, setPursuitSlots] = useState(0);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [promoCode, setPromoCode] = useState('');
  const [quote, setQuote] = useState(null);
  const [isLoadingQuote, setIsLoadingQuote] = useState(false);
  const [isStarting, setIsStarting] = useState(false);

  // Pick the plan list based on the user's org type
  useEffect(() => {
    fetch(`${API_URL}/api/billing/plans`)
      .then((r) => r.json())
      .then((data) => {
        setFoundingCoupon(data.founding_member_coupon);
        const isConsultant = user?.role === 'consultant' || user?.role === 'CONSULTANT';
        const list = isConsultant
          ? data.consultant_plans
          : data.contractor_plans;
        setPlans(list || []);
        const defaultPlan = (list || []).find((p) => p.popular) || (list || [])[0];
        if (defaultPlan) {
          setSelectedPlanId(defaultPlan.id);
          setSeats(defaultPlan.included_seats);
          setPursuitSlots(defaultPlan.included_pursuits);
        }
      });
  }, [user]);

  // Pull current subscription state
  useEffect(() => {
    if (!authToken) return;
    fetch(`${API_URL}/api/billing/subscription`, {
      headers: { Authorization: `Bearer ${authToken}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then(setOrgState)
      .catch(() => {});
  }, [authToken]);

  // If this org has a negotiated custom price, lock the selection to the
  // plan the Super Admin assigned (drives feature access) instead of the
  // "most popular" catalog default — the custom-price card below is what
  // actually gets shown, so this just makes checkout target the right plan.
  const hasCustomPrice = !!(orgState && (orgState.custom_monthly_fee != null || orgState.custom_annual_fee != null));
  useEffect(() => {
    if (!hasCustomPrice || !plans.length || !orgState?.subscription_plan) return;
    const assignedPlan = plans.find((p) => p.id === orgState.subscription_plan);
    if (assignedPlan) {
      setSelectedPlanId(assignedPlan.id);
      setSeats(assignedPlan.included_seats);
      setPursuitSlots(assignedPlan.included_pursuits);
    }
  }, [hasCustomPrice, plans, orgState]);

  // Re-quote whenever selection changes
  const fetchQuote = useCallback(async () => {
    if (!selectedPlanId) return;
    setIsLoadingQuote(true);
    try {
      const res = await fetch(`${API_URL}/api/billing/quote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          plan_id: selectedPlanId,
          seats,
          pursuit_slots: pursuitSlots,
          billing_cycle: billingCycle,
        }),
      });
      if (res.ok) setQuote(await res.json());
    } finally {
      setIsLoadingQuote(false);
    }
  }, [selectedPlanId, seats, pursuitSlots, billingCycle]);

  useEffect(() => { fetchQuote(); }, [fetchQuote]);

  const startCheckout = async () => {
    if (!selectedPlanId) return;
    setIsStarting(true);
    try {
      const res = await fetch(`${API_URL}/api/billing/subscribe/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          plan_id: selectedPlanId,
          seats,
          pursuit_slots: pursuitSlots,
          billing_cycle: billingCycle,
          promo_code: promoCode || null,
          origin_url: `${window.location.origin}${user?.workspaceId ? `/${user.workspaceId}` : ''}`,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(`Could not start checkout: ${err.detail || res.statusText}`);
        return;
      }
      const data = await res.json();
      if (data.session_id) {
        recordPendingCheckout('SUBSCRIPTION', data.session_id);
      }
      window.location.href = data.url;
    } catch (e) {
      console.error('Checkout error:', e);
      alert('Could not start checkout');
    } finally {
      setIsStarting(false);
    }
  };

  const selectedPlan = plans.find((p) => p.id === selectedPlanId);
  const isCustom = selectedPlan?.is_custom;

  return (
    <div className="min-h-screen bg-slate-50 p-6 md:p-10" data-testid="subscribe-page">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <Button variant="ghost" onClick={() => navigate(user?.workspaceId ? `/${user.workspaceId}/settings` : '/admin/dashboard')} className="mb-4">
            ← Back to Settings
          </Button>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2"
              style={{ fontFamily: 'Manrope, sans-serif' }}>
            Choose Your Plan
          </h1>
          <p className="text-slate-600">
            {orgState?.is_trial
              ? `You're on a trial. Pick a plan to continue after ${orgState.trial_expires_at?.split('T')[0] || 'trial end'}.`
              : 'Upgrade or change your subscription anytime.'}
          </p>
        </div>

        {/* Billing cycle toggle */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex bg-white border border-slate-200 rounded-lg p-1">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={cn(
                'px-6 py-2 rounded text-sm font-medium transition-colors',
                billingCycle === 'monthly' ? 'bg-teal-700 text-white' : 'text-slate-600',
              )}
              data-testid="cycle-monthly-btn"
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('annual')}
              className={cn(
                'px-6 py-2 rounded text-sm font-medium transition-colors',
                billingCycle === 'annual' ? 'bg-teal-700 text-white' : 'text-slate-600',
              )}
              data-testid="cycle-annual-btn"
            >
              Annual <Badge variant="secondary" className="ml-2 bg-emerald-100 text-emerald-700">2 mo free</Badge>
            </button>
          </div>
        </div>

        {/* Negotiated custom price — replaces the catalog grid entirely */}
        {hasCustomPrice && (() => {
          const cycleFee = billingCycle === 'annual' ? orgState.custom_annual_fee : orgState.custom_monthly_fee;
          const fallbackFee = billingCycle === 'annual' ? selectedPlan?.base_annual : selectedPlan?.base_monthly;
          const displayFee = cycleFee != null ? cycleFee : fallbackFee;
          return (
            <Card className="border-teal-200 shadow-sm max-w-lg mx-auto" data-testid="custom-price-card">
              <CardContent className="p-6 text-center space-y-4">
                <Badge className="bg-teal-100 text-teal-700">Your Negotiated Plan</Badge>
                <h3 className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {selectedPlan?.name || orgState.subscription_plan}
                </h3>
                {displayFee != null ? (
                  <p className="text-4xl font-bold text-slate-900">
                    ${displayFee.toLocaleString()}
                    <span className="text-base font-normal text-slate-500">/{billingCycle === 'annual' ? 'yr' : 'mo'}</span>
                  </p>
                ) : (
                  <p className="text-sm text-slate-500">Contact your account manager for pricing on this cycle.</p>
                )}
                {orgState.custom_price_note && (
                  <p className="text-xs text-slate-400">{orgState.custom_price_note}</p>
                )}
                <Button
                  onClick={startCheckout}
                  disabled={isStarting || displayFee == null}
                  className="w-full bg-teal-700 hover:bg-teal-800 text-white text-base py-6"
                  data-testid="start-subscription-btn"
                >
                  {isStarting ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Redirecting to Stripe...</>
                  ) : (
                    <><CheckCircle2 className="w-5 h-5 mr-2" /> Continue to Stripe Checkout</>
                  )}
                </Button>
              </CardContent>
            </Card>
          );
        })()}

        {/* Plan cards */}
        {!hasCustomPrice && (
        <>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {plans.map((p) => {
            const Icon = PLAN_ICONS[p.id] || Building2;
            const selected = p.id === selectedPlanId;
            const price = (billingCycle === 'annual' && p.base_annual)
              ? p.base_annual
              : p.base_monthly;
            return (
              <button
                key={p.id}
                onClick={() => {
                  setSelectedPlanId(p.id);
                  setSeats(p.included_seats);
                  setPursuitSlots(p.included_pursuits);
                }}
                className={cn(
                  'relative p-6 rounded-lg border-2 text-left transition-all',
                  selected ? 'border-teal-600 bg-teal-50 shadow-md' : 'border-slate-200 bg-white hover:border-slate-300',
                  p.is_custom && 'border-dashed',
                )}
                data-testid={`plan-card-${p.id}`}
              >
                {p.popular && (
                  <Badge className="absolute -top-2 left-4 bg-teal-700 text-white">Popular</Badge>
                )}
                <Icon className="w-6 h-6 text-teal-700 mb-3" />
                <h3 className="text-lg font-semibold text-slate-900 mb-1">{p.name}</h3>
                <p className="text-xs text-slate-500 mb-3">{p.description}</p>
                {p.is_custom ? (
                  <p className="text-2xl font-bold text-slate-900">Custom</p>
                ) : (
                  <p className="text-2xl font-bold text-slate-900">
                    ${price}<span className="text-sm font-normal text-slate-500">/{billingCycle === 'annual' ? 'yr' : 'mo'}</span>
                  </p>
                )}
                <p className="text-xs text-slate-500 mt-2">
                  {p.included_seats} seats · {p.included_pursuits} pursuits
                </p>
              </button>
            );
          })}
        </div>

        {/* Configuration + quote */}
        {selectedPlan && !isCustom && (
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="p-6 space-y-5">
              <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Configure Your Plan
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Total Seats</Label>
                  <Input
                    type="number"
                    min={selectedPlan.included_seats}
                    value={seats}
                    onChange={(e) => setSeats(Math.max(selectedPlan.included_seats, parseInt(e.target.value || '0')))}
                    data-testid="seats-input"
                  />
                  <p className="text-xs text-slate-500">
                    {selectedPlan.included_seats} included · ${selectedPlan.per_seat_monthly}/mo per extra seat
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Pursuit Slots</Label>
                  <Input
                    type="number"
                    min={selectedPlan.included_pursuits}
                    value={pursuitSlots}
                    onChange={(e) => setPursuitSlots(Math.max(selectedPlan.included_pursuits, parseInt(e.target.value || '0')))}
                    data-testid="pursuits-input"
                  />
                  <p className="text-xs text-slate-500">
                    {selectedPlan.included_pursuits} included · ${selectedPlan.per_pursuit_monthly}/mo per extra slot
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Tag className="w-4 h-4 text-amber-600" />
                  Promo Code (optional)
                </Label>
                <Input
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value)}
                  placeholder={foundingCoupon ? `e.g. ${foundingCoupon.promo_code} for ${foundingCoupon.percent_off}% × ${foundingCoupon.duration_in_months}mo` : 'Enter code'}
                  data-testid="promo-code-input"
                />
              </div>

              {/* Quote summary */}
              <div className="bg-slate-50 rounded-lg p-4 space-y-2 border border-slate-200">
                {isLoadingQuote ? (
                  <div className="flex items-center gap-2 text-sm text-slate-500">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Calculating...
                  </div>
                ) : quote && (
                  <>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Base ({selectedPlan.name}, {billingCycle})</span>
                      <span className="font-medium">${quote.base_amount.toFixed(2)}</span>
                    </div>
                    {quote.extra_seats > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">+{quote.extra_seats} extra seats</span>
                        <span className="font-medium">${quote.seat_overage_total.toFixed(2)}</span>
                      </div>
                    )}
                    {quote.extra_pursuits > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">+{quote.extra_pursuits} extra pursuit slots</span>
                        <span className="font-medium">${quote.pursuit_overage_total.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-base font-semibold text-slate-900 pt-2 border-t border-slate-200">
                      <span>Total {billingCycle === 'annual' ? '(per year)' : '(per month)'}</span>
                      <span data-testid="quote-total">${quote.total_amount.toFixed(2)}</span>
                    </div>
                    <p className="text-xs text-slate-400">Promo codes apply during Stripe Checkout.</p>
                  </>
                )}
              </div>

              <Button
                onClick={startCheckout}
                disabled={isStarting || isLoadingQuote || !quote}
                className="w-full bg-teal-700 hover:bg-teal-800 text-white text-base py-6"
                data-testid="start-subscription-btn"
              >
                {isStarting ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Redirecting to Stripe...</>
                ) : (
                  <><CheckCircle2 className="w-5 h-5 mr-2" /> Continue to Stripe Checkout</>
                )}
              </Button>
            </CardContent>
          </Card>
        )}

        {isCustom && (
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="p-6 text-center">
              <Crown className="w-12 h-12 text-purple-600 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2"
                  style={{ fontFamily: 'Manrope, sans-serif' }}>
                Enterprise — Let&apos;s Talk
              </h3>
              <p className="text-sm text-slate-600 mb-4">
                Enterprise plans include custom integrations, SLA guarantees, and dedicated support.
                Reach out for a tailored quote.
              </p>
              <Button onClick={() => window.location.href = 'mailto:sales@fedbd.ai'}
                      className="bg-purple-700 hover:bg-purple-800 text-white">
                Contact Sales
              </Button>
            </CardContent>
          </Card>
        )}
        </>
        )}
      </div>
    </div>
  );
}
